import React, { useState } from 'react';
import { CheckCircleIcon, DollarSignIcon, TruckIcon, CalendarIcon, FileTextIcon, EyeIcon, SearchIcon, FilterIcon, DownloadIcon } from 'lucide-react';
interface Document {
  type: string;
  fileName: string;
}
interface ApprovedRequest {
  id: string;
  truckingCompany: string;
  jobNumber: string;
  jobName: string;
  completionDate: string;
  requestDate: string;
  approvalDate: string;
  amount: number;
  invoiceNumber: string;
  status: string;
  priority: string;
  contractor: string;
  contractorRating: string;
  completedLoads: number;
  totalHours: number;
  documents: Document[];
  notes: string;
  paymentTerms: string;
  factorRate: number;
  achFileName: string;
  achGenerationDate: string;
  expectedPaymentDate: string;
}
interface ApprovedRequestsProps {
  requests: ApprovedRequest[];
  onViewDetails: (requestId: string) => void;
}
export const ApprovedRequests: React.FC<ApprovedRequestsProps> = ({
  requests,
  onViewDetails
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('approvalDate');
  const [sortDirection, setSortDirection] = useState('desc');
  // Filter and sort requests
  const filteredRequests = requests.filter(request => {
    const matchesSearch = searchTerm === '' || request.truckingCompany.toLowerCase().includes(searchTerm.toLowerCase()) || request.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) || request.jobName.toLowerCase().includes(searchTerm.toLowerCase()) || request.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    if (dateFilter === 'all') return matchesSearch;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const approvalDate = new Date(request.approvalDate);
    approvalDate.setHours(0, 0, 0, 0);
    if (dateFilter === 'today') {
      return matchesSearch && approvalDate.getTime() === today.getTime();
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      return matchesSearch && approvalDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      return matchesSearch && approvalDate >= monthAgo;
    }
    return matchesSearch;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'amount') {
      comparison = a.amount - b.amount;
    } else if (sortBy === 'approvalDate') {
      comparison = new Date(a.approvalDate).getTime() - new Date(b.approvalDate).getTime();
    } else if (sortBy === 'truckingCompany') {
      comparison = a.truckingCompany.localeCompare(b.truckingCompany);
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-2928">
      <div className="p-4 border-b" data-id="element-2929">
        <h2 className="text-lg font-medium flex items-center gap-2" data-id="element-2930">
          <CheckCircleIcon size={20} className="text-[#0F2A52]" data-id="element-2931" />
          Approved Factoring Requests
        </h2>
      </div>
      {/* Filters */}
      <div className="p-4 border-b bg-gray-50" data-id="element-2932">
        <div className="flex flex-wrap gap-4" data-id="element-2933">
          <div className="flex-1 min-w-[200px]" data-id="element-2934">
            <div className="relative" data-id="element-2935">
              <input type="text" placeholder="Search by company, job, or invoice..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2936" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-2937" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-2938">
            <FilterIcon size={18} className="text-gray-500" data-id="element-2939" />
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" data-id="element-2940">
              <option value="all" data-id="element-2941">All Time</option>
              <option value="today" data-id="element-2942">Today</option>
              <option value="week" data-id="element-2943">Past Week</option>
              <option value="month" data-id="element-2944">Past Month</option>
            </select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto" data-id="element-2945">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-2946">
          <thead className="bg-gray-50" data-id="element-2947">
            <tr data-id="element-2948">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('truckingCompany')} data-id="element-2949">
                <div className="flex items-center gap-1" data-id="element-2950">
                  Company
                  {sortBy === 'truckingCompany' && <span data-id="element-2951">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2952">
                Job Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('approvalDate')} data-id="element-2953">
                <div className="flex items-center gap-1" data-id="element-2954">
                  Dates
                  {sortBy === 'approvalDate' && <span data-id="element-2955">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-2956">
                <div className="flex items-center gap-1" data-id="element-2957">
                  Amount
                  {sortBy === 'amount' && <span data-id="element-2958">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2959">
                ACH Details
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2960">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-2961">
            {filteredRequests.length > 0 ? filteredRequests.map(request => <tr key={request.id} className="hover:bg-gray-50" data-id="element-2962">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2963">
                    <div className="flex items-center" data-id="element-2964">
                      <div className="flex-shrink-0 h-10 w-10 bg-green-600 rounded-full flex items-center justify-center text-white" data-id="element-2965">
                        <TruckIcon size={20} data-id="element-2966" />
                      </div>
                      <div className="ml-4" data-id="element-2967">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2968">
                          {request.truckingCompany}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2969">
                          {request.invoiceNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-2970">
                    <div className="text-sm text-gray-900 font-medium" data-id="element-2971">
                      {request.jobName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2972">
                      {request.jobNumber}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2973">
                      Contractor: {request.contractor}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2974">
                    <div className="flex flex-col" data-id="element-2975">
                      <span className="text-sm text-gray-900 flex items-center gap-1" data-id="element-2976">
                        <CheckCircleIcon size={14} className="text-green-500" data-id="element-2977" />
                        Approved: {formatDate(request.approvalDate)}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center gap-1" data-id="element-2978">
                        <CalendarIcon size={14} className="text-gray-400" data-id="element-2979" />
                        Payment: {formatDate(request.expectedPaymentDate)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2980">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2981">
                      $
                      {request.amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-2982">
                      <DollarSignIcon size={12} className="text-green-500" data-id="element-2983" />
                      {request.factorRate}% factor rate
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2984">
                    <div className="flex items-center gap-1 text-sm text-gray-900" data-id="element-2985">
                      <div size={14} className="text-[#0F2A52]" data-id="element-2986" />
                      {request.achFileName}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-2987">
                      Generated: {formatDate(request.achGenerationDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2988">
                    <div className="flex flex-col gap-2 items-end" data-id="element-2989">
                      <button onClick={() => onViewDetails(request.id)} className="text-[#0F2A52] hover:text-[#0F2A52]/80 px-2 py-1 rounded-md flex items-center gap-1 ml-auto" data-id="element-2990">
                        <EyeIcon size={16} data-id="element-2991" />
                        View Details
                      </button>
                      <button className="text-[#F58220] hover:text-[#F58220]/80 px-2 py-1 rounded-md flex items-center gap-1 ml-auto" data-id="element-2992">
                        <DownloadIcon size={16} data-id="element-2993" />
                        ACH File
                      </button>
                    </div>
                  </td>
                </tr>) : <tr data-id="element-2994">
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500" data-id="element-2995">
                  No approved requests match your filters
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
};